import {DateTime} from 'luxon';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {getCalendarDays, getSortedWeekdayNames, getSortedWeekdays} from '../../../helpers/calendarHelpers';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {StartAndEndDates} from '../../../models/dates/dateIntervalModel';
import {DatePickerGridItem} from './datePickerGridItem';

/*
 * Props.
 */

interface DatePickerGridProps {
  focusedMonth: DateTime;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  selectedDate?: DateTime;
  selectedDates?: StartAndEndDates;
  highlightedDate?: DateTime;
  minDate?: DateTime;
  maxDate?: DateTime;
  onDateSelect?: HandlerOf<DateTime>;
  primaryColor: string | undefined;
  secondaryColor: string | undefined;
  disabledColor: string | undefined;
  backgroundHoverColor: string | undefined;
  hideDifferentMonth?: boolean;
}

/*
 * Style.
 */

const StyledGridDiv = styled.div`
  display: grid;
  grid-template-columns: repeat(7, min-content);
  grid-gap: 5px;
  justify-content: space-between;
  justify-items: center;
`;

interface WeekdayStyleProps {
  $color?: string;
}
const StyledWeekdayDiv = styled.div<WeekdayStyleProps>`
  flex: 1;
  text-align: center;
  color: ${(p) => p.$color || p.theme.greys.shade70};
  font-size: ${fontSizes.small};
`;

/*
 * Component.
 */

export const DatePickerGrid: FC<DatePickerGridProps> = (props) => {
  const {calendarWeekStartDay, secondaryColor} = props;
  const weekdays = getSortedWeekdays(calendarWeekStartDay);
  const weekdaysNames = getSortedWeekdayNames(calendarWeekStartDay);

  return (
    <StyledGridDiv>
      {renderWeekdays(weekdays, secondaryColor)}
      {renderDays(props, weekdays, weekdaysNames[weekdaysNames.length - 1])}
    </StyledGridDiv>
  );
};

/** Render the weekday headers. */
function renderWeekdays(weekdays: ReadonlyArray<number>, color: string | undefined) {
  return weekdays.map((weekDay) => (
    <StyledWeekdayDiv $color={color} key={`weekday-${weekDay}`}>
      {weekdayRenders[weekDay]}
    </StyledWeekdayDiv>
  ));
}

/** Render all the calendar days. */
function renderDays(
  props: DatePickerGridProps,
  weekdays: ReadonlyArray<number>,
  lastWeekDay: CalendarWeekDaysEnum,
) {
  const {
    focusedMonth,
    selectedDate,
    selectedDates,
    highlightedDate,
    minDate,
    maxDate,
    hideDifferentMonth,
    onDateSelect,
    calendarWeekStartDay,
  } = props;
  const {primaryColor, disabledColor, backgroundHoverColor} = props;
  const days = getCalendarDays(focusedMonth, weekdays);
  return days.map((day) => {
    // Check if this day is in range.
    const isAfterMinDate = !minDate || minDate < day.endOf('day');
    const isBeforeMaxDate = !maxDate || maxDate > day.startOf('day');
    const {weekdayLong} = day;
    // Calling weekdayLong mutates prototype on date object
    // https://github.com/moment/luxon/issues/323
    // @ts-expect-error
    day.weekData = null; // eslint-disable-line no-param-reassign

    return (
      <DatePickerGridItem
        key={day.valueOf()}
        day={day}
        isDifferentMonth={day.month !== focusedMonth.month}
        isSelectable={isAfterMinDate && isBeforeMaxDate}
        isSelected={isSelected(day, selectedDate, selectedDates)}
        isHighlighted={areDatesEqual(day, highlightedDate)}
        isHighlightedFromRange={isDateWithinRange(day, selectedDates?.startDate, selectedDates?.endDate)}
        isStartDateHighlightedFromRange={
          areDatesEqual(day, selectedDates?.startDate) &&
          areDatesDifferentAndExists(selectedDates?.startDate, selectedDates?.endDate)
        }
        isEndDateHighlightedFromRange={
          areDatesEqual(day, selectedDates?.endDate) &&
          areDatesDifferentAndExists(selectedDates?.startDate, selectedDates?.endDate)
        }
        isFirstDayInWeek={
          weekdayLong.toLowerCase() === calendarWeekStartDay || day.equals(focusedMonth.startOf('month'))
        }
        isLastDayInWeek={
          weekdayLong.toLowerCase() === lastWeekDay || day.equals(focusedMonth.endOf('month').startOf('day'))
        }
        onSelect={() => onDateSelect && onDateSelect(day)}
        color={primaryColor}
        disabledColor={disabledColor}
        backgroundHoverColor={backgroundHoverColor}
        hideDifferentMonth={hideDifferentMonth}
      />
    );
  });
}

/*
 * Localization.
 */

type WeekdayRenders = {[key: number]: ReactNode};
const weekdayRenders: WeekdayRenders = {
  1: <Trans id="day-short-monday">Mon</Trans>,
  2: <Trans id="day-short-tuesday">Tue</Trans>,
  3: <Trans id="day-short-wednesday">Wed</Trans>,
  4: <Trans id="day-short-thursday">Thu</Trans>,
  5: <Trans id="day-short-friday">Fri</Trans>,
  6: <Trans id="day-short-saturday">Sat</Trans>,
  7: <Trans id="day-short-sunday">Sun</Trans>,
};

/*
 * Date helpers.
 */

function areDatesEqual(date1: DateTime, date2?: DateTime) {
  return Boolean(date2 && date1.startOf('day').equals(date2.startOf('day')));
}

function areDatesDifferentAndExists(start?: DateTime, end?: DateTime) {
  if (!start || !end) {
    return false;
  }

  return !start.startOf('day').equals(end.startOf('day'));
}

function isDateWithinRange(date: DateTime, start?: DateTime, end?: DateTime) {
  if (!start || !end) {
    return false;
  }

  const dateMillis = date.toMillis();
  const startMillis = start.toMillis();
  const endMillis = end.toMillis();

  return dateMillis >= startMillis && dateMillis <= endMillis;
}

function isSelected(date: DateTime, selectedDate?: DateTime, selectedDates?: StartAndEndDates) {
  if (selectedDate) {
    return areDatesEqual(date, selectedDate);
  }

  if (!selectedDates) {
    return false;
  }

  return areDatesEqual(date, selectedDates.startDate) || areDatesEqual(date, selectedDates.endDate);
}
